import { lazy, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../stores/auth.js';
import { onAuthLostHandler } from '../lib/api.js';

const LoginPage = lazy(async () => ({
  default: (await import('../pages/auth/LoginPage.js')).LoginPage,
}));
const PinPage = lazy(async () => ({
  default: (await import('../pages/auth/PinPage.js')).PinPage,
}));
const RegisterPage = lazy(async () => ({
  default: (await import('../pages/auth/RegisterPage.js')).RegisterPage,
}));

/**
 * Минимальное время показа splash-экрана. Даже если приложение успевает
 * инициализироваться за 100мс, splash продержится не меньше этой длительности —
 * чтобы пользователь успел увидеть фирменную анимацию, а не получил «мигание».
 */
const SPLASH_MIN_MS = 2000;
const SPLASH_SESSION_KEY = 'jb:boot_splash_seen';

/**
 * Фирменный минимальный splash нужен только при первом запуске WebApp-сессии.
 * Обычная навигация или вынужденный reload не должны снова блокировать UI на
 * две секунды. Ключ записываем на уровне модуля, чтобы React StrictMode не
 * превратил двойной вызов state-initializer в пропуск первого splash.
 */
const ENFORCE_SPLASH_MIN = (() => {
  if (typeof window === 'undefined') return true;
  try {
    if (window.sessionStorage.getItem(SPLASH_SESSION_KEY) === '1') return false;
    window.sessionStorage.setItem(SPLASH_SESSION_KEY, '1');
  } catch {
    // При недоступном sessionStorage сохраняем прежнее безопасное поведение.
  }
  return true;
})();

export function AuthGate({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const init = useAuth((s) => s.init);
  const expireSession = useAuth((s) => s.expireSession);
  // Локальный toggle между login и register экранами (без отдельных routes —
  // оба экрана появляются в одном «unauthenticated» состоянии).
  const [mode, setMode] = useState<'login' | 'register'>('login');
  // Истёк ли минимальный таймер splash-экрана.
  const [minSplashElapsed, setMinSplashElapsed] = useState(!ENFORCE_SPLASH_MIN);

  useEffect(() => {
    void init();
    onAuthLostHandler(expireSession);
    const splashTimer = ENFORCE_SPLASH_MIN
      ? window.setTimeout(() => setMinSplashElapsed(true), SPLASH_MIN_MS)
      : null;
    return () => {
      if (splashTimer != null) window.clearTimeout(splashTimer);
      onAuthLostHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Splash показываем пока:
  //   а) реальная инициализация ещё идёт (status='booting'), ИЛИ
  //   б) минимальный таймер ещё не истёк
  if (status === 'booting' || !minSplashElapsed) {
    return <BootingSplash />;
  }
  if (status === 'unauthenticated') {
    return mode === 'register'
      ? <RegisterPage onBack={() => setMode('login')} />
      : <LoginPage onRegister={() => setMode('register')} />;
  }
  if (status === 'pin_required') return <PinPage />;
  return <>{children}</>;
}

/**
 * Splash-экран при первой загрузке (status='booting').
 * Фирменный «окно с ламелями + монограмма JB», ламели плавно «опускаются»
 * волной — визуальная анимация которая совпадает с темой бренда.
 */
function BootingSplash() {
  return (
    <div style={{
      minHeight: '100dvh',
      display: 'grid', placeItems: 'center',
      background: 'var(--bg-soft)',
      padding: 16,
    }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
      }}>
        {/* Логотип-окно. SVG с brass-ламелями и волновой анимацией opacity. */}
        <svg
          width="120" height="120" viewBox="0 0 512 512"
          aria-label="Jalousie Brand"
          style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))' }}
        >
          <defs>
            <radialGradient id="bsBg" cx="50%" cy="38%" r="68%">
              <stop offset="0%" stopColor="#2A241B"/>
              <stop offset="55%" stopColor="#15120D"/>
              <stop offset="100%" stopColor="#080706"/>
            </radialGradient>
            <linearGradient id="bsFrame" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F0D08E"/>
              <stop offset="100%" stopColor="#8A6A30"/>
            </linearGradient>
            <linearGradient id="bsLamel" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F4D795"/>
              <stop offset="65%" stopColor="#A98344"/>
              <stop offset="100%" stopColor="#7A5A2A"/>
            </linearGradient>
          </defs>

          <rect width="512" height="512" rx="96" fill="url(#bsBg)"/>
          <rect x="92" y="96" width="328" height="320" rx="16"
                fill="none" stroke="url(#bsFrame)" strokeWidth="5"/>
          <rect x="108" y="112" width="296" height="288" rx="8" fill="#0E0C09"/>

          {/* Ламели — каждая со своей задержкой анимации opacity */}
          <g style={{ animation: 'jb-splash-bounce 2.4s ease-in-out infinite' }}>
            <rect x="120" y="127" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0s' }}/>
            <rect x="120" y="165" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.1s' }}/>
            <rect x="120" y="203" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.2s' }}/>
            <rect x="120" y="241" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.3s' }}/>
            <rect x="120" y="279" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.4s' }}/>
            <rect x="120" y="317" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.5s' }}/>
            <rect x="120" y="355" width="272" height="30" rx="3" fill="url(#bsLamel)"
                  style={{ animation: 'jb-splash-lamel 1.6s ease-in-out infinite', animationDelay: '0.6s' }}/>
          </g>
        </svg>

        {/* Название — бренд-лого */}
        <img src="/brand/logo.svg" alt="Jalousie" style={{ height: 40, width: 'auto' }} />

        {/* Полоса прогресса под названием — brass-индикатор */}
        <div style={{
          width: 120, height: 3,
          borderRadius: 999,
          background: 'var(--hairline)',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute',
            top: 0, bottom: 0,
            left: '-40%', width: '40%',
            background: 'linear-gradient(90deg, transparent, var(--accent), transparent)',
            animation: 'jb-splash-progress 1.4s ease-in-out infinite',
          }} />
        </div>
      </div>
    </div>
  );
}
